import React from 'react';

export const PublicApiOverview: React.FC = () => {
  const endpoints = [
    { method: 'POST', endpoint: '/register', description: 'Register a new restaurant' },
    { method: 'GET', endpoint: '/restaurants', description: 'Get all restaurants' },
    { method: 'POST', endpoint: '/merchant/config', description: 'Configure merchant settings' },
    { method: 'PUT', endpoint: '/webhook/settings', description: 'Configure order webhooks' },
    { method: 'POST', endpoint: '/menu/upsert', description: 'Add or update menu items' },
    { method: 'POST', endpoint: '/upload', description: 'Upload menu files' },
    { method: 'GET', endpoint: '/menu', description: 'Get menu items' },
    { method: 'GET', endpoint: '/status', description: 'Get upload status' },
    { method: 'POST', endpoint: '/order/create', description: 'Create a new order' },
    { method: 'PUT', endpoint: '/order/status', description: 'Update order status' },
    { method: 'POST', endpoint: '/payment/status', description: 'Update payment status' },
    { method: 'POST', endpoint: '/voice/number/assign', description: 'Assign Number to Order' },
    { method: 'POST', endpoint: '/workflow/setting/voice', description: 'Voice Configuration' },
  ];

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'POST': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'PUT': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case 'DELETE': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-12">
          {/* Header */}
          <section>
            <h1 className="text-4xl font-bold text-[var(--text-primary)] mb-4">
              MenuBuilder API Overview
            </h1>
            <p className="text-[var(--text-secondary)] text-lg">
              The SnapMenu API allows restaurants, POS systems, and third-party platforms to programmatically manage menus, orders, payments, and merchant settings. It's fully testable in a sandbox environment before production use.
            </p>
          </section>

          {/* Description */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Integration Features
            </h2>
            <ul className="list-disc list-inside space-y-2 text-[var(--text-secondary)] ml-4">
              <li>Onboard restaurants</li>
              <li>Manage menus</li>
              <li>Simulate and process orders</li>
              <li>Configure merchant settings</li>
              <li>Set up webhooks</li>
              <li>Manage payments</li>
              <li>Enable AI voice ordering</li>
            </ul>
          </section>

          {/* Base URL */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Base URL
            </h2>
            <div className="bg-[var(--bg-secondary)] rounded-lg p-6 border border-[var(--border-color)]">
              <div className="flex items-center gap-3">
                <span className="text-xs px-3 py-1 rounded-full font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  Sandbox
                </span>
                <code className="text-[var(--accent-color)] font-mono text-sm break-all">
                  https://sandboxmenubuilder.snapmenu.ai/api/menu-builder
                </code>
              </div>
            </div>
          </section>

          {/* Authentication */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Authentication
            </h2>
            <div className="space-y-3">
              <p className="text-[var(--text-secondary)]">
                All requests require two headers:
              </p>
              <ul className="list-disc list-inside space-y-1 text-[var(--text-secondary)] ml-4">
                <li><strong>api-key:</strong> Your API key</li>
                <li><strong>restaurant-id:</strong> Your restaurant ID</li>
              </ul>
              
              <p className="text-[var(--text-secondary)] mt-4 mb-2">
                <strong>Example Request:</strong>
              </p>
              <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
                <pre className="text-sm font-mono overflow-x-auto">
                  <code className="text-[var(--text-primary)]">
                    {`curl -X GET "https://sandboxmenubuilder.snapmenu.ai/api/menu-builder/restaurant" \\
  -H "api-key: a28beb13-644a-4efe-b66a-27e2b3b752c1" \\
  -H "restaurant-id: 123e4567-e89b-12d3-a456-426614174000"`}
                  </code>
                </pre>
              </div>

            </div>
          </section>

          {/* Endpoints Summary */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Endpoints Summary
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-[var(--border-color)]">
                <thead>
                  <tr className="bg-[var(--bg-secondary)]">
                    <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Method</th>
                    <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Endpoint</th>
                    <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoints.map((endpoint, index) => (
                    <tr key={index} className="hover:bg-[var(--bg-secondary)]/50 transition-colors">
                      <td className="border border-[var(--border-color)] px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getMethodColor(endpoint.method)}`}>
                          {endpoint.method}
                        </span>
                      </td>
                      <td className="border border-[var(--border-color)] px-4 py-3">
                        <code className="font-mono text-sm text-[var(--text-primary)]">
                          {endpoint.endpoint}
                        </code>
                      </td>
                      <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">
                        {endpoint.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Request Example */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Request Example
            </h2>
            <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
              <pre className="text-sm font-mono overflow-x-auto">
                <code className="text-[var(--text-primary)]">
                  {`curl -X GET "https://sandboxmenubuilder.snapmenu.ai/api/menu-builder/restaurant" \\
  -H "api-key: YOUR_API_KEY" \\
  -H "restaurant-id: YOUR_RESTAURANT_ID"`}
                </code>
              </pre>
            </div>
          </section>

          {/* Response Example */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Response Example
            </h2>
            <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
              <pre className="text-sm font-mono overflow-x-auto">
                <code className="text-[var(--text-primary)]">
                  {`[
  {
    "restaurantId": "a2959265-3cc2-4565-98ee-f983584a639f",
    "name": "Sample Restaurant",
    "phone": "1234567890",
    "address": "123 Main Street",
    "state": "California",
    "zipcode": "90210",
    "country": "United States",
    "owner": "John Doe",
    "alternativePhone": "1234567891",
    "cuisineType": "Italian"
  }
]`}
                </code>
              </pre>
            </div>
          </section>

          {/* Error Handling */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Error Handling
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-[var(--border-color)]">
                <thead>
                  <tr className="bg-[var(--bg-secondary)]">
                    <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Code</th>
                    <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-[var(--bg-secondary)]/50">
                    <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-green-600 dark:text-green-400">200</td>
                    <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Success</td>
                  </tr>
                  <tr className="hover:bg-[var(--bg-secondary)]/50">
                    <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-amber-600 dark:text-amber-400">400</td>
                    <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Bad Request</td>
                  </tr>
                  <tr className="hover:bg-[var(--bg-secondary)]/50">
                    <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-red-600 dark:text-red-400">401</td>
                    <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Unauthorized</td>
                  </tr>
                  <tr className="hover:bg-[var(--bg-secondary)]/50">
                    <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-red-600 dark:text-red-400">404</td>
                    <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Not Found</td>
                  </tr>
                  <tr className="hover:bg-[var(--bg-secondary)]/50">
                    <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-red-600 dark:text-red-400">500</td>
                    <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Server Error</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Error Format */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Error Response Example
            </h2>
            <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
              <pre className="text-sm font-mono overflow-x-auto">
                <code className="text-[var(--text-primary)]">
                  {`{
  "error": "Invalid authentication credentials",
  "status": false
}`}
                </code>
              </pre>
            </div>
          </section>

          {/* Support */}
          <section>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Support
            </h2>
            <p className="text-[var(--text-secondary)] mb-3">
              For technical support or API key requests, please contact:
            </p>
            <p className="text-[var(--accent-color)]">
              <strong>Email:</strong> support@snapmenu.ai
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};
